import { uniq } from "lodash";
import { useCallback, useMemo } from "react";

import {
  DependencyType,
  ModelListItemFragment,
  QueryDependencyInput,
} from "@/apollo/types";

import { useModelEditorDependencyReferences } from "./ModelEditorStore";
import {
  useModelDepedenciesMapGetter,
  useModelDependenciesLoaded,
  useModelDependenciesMap,
} from "./providers/ModelDependenciesProvider";
import { useRawViews } from "./useRawViews";

const weldTagRegex = /{{[\w][\w.-]*}}/g;
const bracketsRegex = /[{}]/g;

export function extractWeldTags(sqlQuery: string) {
  if (sqlQuery.trim() === "") return [];

  const weldTags = removeSqlComments(sqlQuery)
    .match(weldTagRegex)
    ?.map((match) => match.replace(bracketsRegex, ""));

  return uniq(weldTags ?? []);
}

type ReferenceMapValueType =
  | ModelListItemFragment["dwSync"]
  | ModelListItemFragment["dwTable"]
  | ReturnType<typeof useRawViews>["rawViews"][number];

export function toQueryDependencyInput(
  weldTag: string,
  ref: ReferenceMapValueType,
) {
  if (ref) {
    if (
      ref.__typename === "ModelViewReference" ||
      ref.__typename === "MaterializedTableReference"
    ) {
      return {
        dwItemId: ref.modelId,
        weldTag: weldTag,
        type: ref.type,
      } as QueryDependencyInput;
    }
    if (ref.__typename === "RawViewReference") {
      return {
        dwItemId: ref.viewId,
        weldTag: weldTag,
        type: DependencyType.RawView,
      } as QueryDependencyInput;
    }
  }
  return {
    dwItemId: "@missing",
    type: DependencyType.MissingReference,
    weldTag: weldTag,
  } as QueryDependencyInput;
}

export const useQueryDependencies = () => {
  const referenceMap = useModelDependenciesMap();
  const weldTags = useModelEditorDependencyReferences();
  return useMemo(() => {
    return weldTags.map((weldTag) => {
      const ref = referenceMap.get(weldTag);
      return toQueryDependencyInput(weldTag, ref);
    });
  }, [weldTags, referenceMap]);
};

export function useGetQueryDependencies() {
  const referenceMap = useModelDependenciesMap();
  return useCallback(
    (weldTags: string[]) => {
      return weldTags.map((weldTag) => {
        const ref = referenceMap.get(weldTag);
        return toQueryDependencyInput(weldTag, ref);
      });
    },
    [referenceMap],
  );
}

/**
 * Like `useGetQueryDependencies`, but waits for raw views and published models first, so a query
 * submitted while they load does not send its references as missing.
 */
export function useResolveQueryDependencies() {
  const dependenciesLoaded = useModelDependenciesLoaded();
  const getReferenceMap = useModelDepedenciesMapGetter();
  return useCallback(
    async (weldTags: string[]) => {
      await dependenciesLoaded;
      const referenceMap = getReferenceMap();
      return weldTags.map((weldTag) =>
        toQueryDependencyInput(weldTag, referenceMap.get(weldTag)),
      );
    },
    [dependenciesLoaded, getReferenceMap],
  );
}

/**
 * Removes any SQL comments from a string query.
 *
 * In most SQL dialects, single-line comments are started with "--"
 * and always run until the end of the line.
 *
 * Multi-line comments are enclosed in `/*` and `*\/`, similar to JS,
 * because they can't be nested, we use a non-greedy match.
 *
 * Check tests for specific examples.
 */
export const removeSqlComments = (sql: string) => {
  return sql
    .replace(/(--.*\s?)/g, "") // Single-Line comments
    .replace(/\/\*([\s\S]*?)\*\//gm, ""); // Multi-Line comments
};
